import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Close } from "@mui/icons-material";
import { useAuth } from "../../hooks/AuthProvider";
import { TEMPLATE_ENDPOINT } from "../../config/urls";

const BUTTON_REPLY_OPTIONS = [
  { label: "Continue Next", value: "continue_next" },
  { label: "Live Chat (Exits Drip)", value: "live_chat_exit" },
  { label: "Exit", value: "exit" },
];
const NO_REPLY_OPTIONS = [
  { label: "Continue Next", value: "continue_next" },
  { label: "Exit", value: "exit" },
];

const CreateDripStepModal = ({
  open,
  handleClose,
  triggerType,
  addDripStep,
  firstStep,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [templateOptions, setTemplateOptions] = useState([]);

  // Common Inputs
  const [stepName, setStepName] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [delay, setDelay] = useState(24);

  // Trigger Specific Inputs
  const [waitForUserReply, setWaitForUserReply] = useState(false);
  const [buttonReplyOptions, setButtonReplyOptions] = useState({});
  const [noReplyOption, setNoReplyOption] = useState(NO_REPLY_OPTIONS[0]);

  const { getValidToken } = useAuth();

  const fetchTemplatesData = useCallback(async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(
        `${TEMPLATE_ENDPOINT}?limit=1000&ordering=-last_edited`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch templates data");
      const jsonData = await response.json();
      const approvedTemplates = {
        ...jsonData,
        results: jsonData.results.filter(
          (template) => template.status === "APPROVED" && template.active
        ),
      };

      setTemplateOptions(
        approvedTemplates.results.map((item) => {
          const allButtons = item?.template_preview_json?.allButtons;

          return {
            id: item.template_id,
            name: item.template_name,
            buttons: Array.isArray(allButtons)
              ? allButtons
                  ?.filter((button) => button.type === "custom")
                  ?.map((button) => {
                    return {
                      id: button?.id,
                      text: button?.data?.buttonText || "",
                    };
                  })
              : [],
          };
        })
      );
    } catch (error) {
      console.error("Error fetching templates data:", error.message);
    }
  }, [getValidToken]);

  const resetForm = () => {
    setStepName("");
    setSelectedTemplate(null);
    setDelay(24);
    setWaitForUserReply(false);
    setButtonReplyOptions({});
    setNoReplyOption(NO_REPLY_OPTIONS[0]);
  };

  const canSubmit = () => {
    // Common validations
    if (!stepName || !selectedTemplate) return false;
    if (!Number.isInteger(delay) || delay <= 0) return false;

    // Id reply based
    if (triggerType?.value === "reply_based") {
      // Reply-based validations
      if (!waitForUserReply) return true;

      // Ensure buttonReplyOptions cover all buttons
      const allButtons =
        selectedTemplate?.buttons?.filter((button) =>
          ["Interested", "Not Interested"].includes(button.text)
        ) || [];
      const allHaveOptions =
        allButtons.length === 0 ||
        allButtons.every(
          (btn) => buttonReplyOptions && buttonReplyOptions[btn.id]
        );

      // Ensure a no-reply option is selected
      const hasNoReply = !!noReplyOption;

      return allHaveOptions && hasNoReply;
    } else {
      // If not reply-based, no need for reply-related checks
      return true;
    }
  };

  const isTemplateOptionDisabled = (option) => {
    const buttons = option?.buttons || [];

    if (buttons.length === 0) {
      return false;
    } else {
      const interestedButtonPresent = buttons.some(
        (button) => button.text === "Interested"
      );

      const notInterestedButtonPresent = buttons.some(
        (button) => button.text === "Not Interested"
      );

      if (interestedButtonPresent || notInterestedButtonPresent) {
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    fetchTemplatesData();
  }, []);

  const handleCancel = () => {
    setError(null);
    handleClose();
  };

  const handleTemplateChange = (value) => {
    setSelectedTemplate(value);
  };

  const handleSubmit = () => {
    if (canSubmit()) {
      addDripStep({
        stepName,
        template: selectedTemplate,
        delay,
        triggerType,
        waitForUserReply,
        buttonReplyOptions,
        noReplyOption,
      });
      resetForm();
      handleClose();
    }
  };

  // Reset Lower fields if selected template changes
  useEffect(() => {
    setWaitForUserReply(false);
    setButtonReplyOptions({});
    setNoReplyOption(NO_REPLY_OPTIONS[0]);
  }, [selectedTemplate]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 1204 }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Add Drip Step
        <IconButton aria-label="close" onClick={handleCancel}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Common Inputs */}
        <Box display="flex" flexDirection="column" gap={2} pt={2}>
          {error && (
            <Alert severity="error">{typeof error === "string" && error}</Alert>
          )}

          {/* Step Name */}
          <TextField
            label="Step Name"
            variant="outlined"
            value={stepName}
            onChange={(e) => setStepName(e.target.value)}
            fullWidth
            required
          />

          {/* Select Template */}
          <Autocomplete
            sx={{ width: "100%" }}
            options={templateOptions}
            value={selectedTemplate}
            onChange={(event, newValue) => handleTemplateChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select a template"
                variant="outlined"
                required
              />
            )}
            getOptionLabel={(option) => option.name}
            getOptionDisabled={(option) => isTemplateOptionDisabled(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No templates found"
          />

          {/* Delay */}
          <TextField
            label="Reply Delay (hours)"
            variant="outlined"
            type="number"
            value={delay}
            onChange={(e) => setDelay(parseInt(e.target.value))}
            fullWidth
          />
        </Box>

        {/* Trigger Specific Inputs */}
        {triggerType && triggerType.value === "reply_based" && (
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            {/* Wait for User Reply Checkbox */}
            {selectedTemplate?.buttons &&
              selectedTemplate?.buttons?.filter((button) =>
                ["Interested", "Not Interested"].includes(button?.text)
              ).length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={waitForUserReply}
                      onChange={(e) => setWaitForUserReply(e.target.checked)}
                    />
                  }
                  label="Wait for User Reply"
                  sx={{ width: "fit-content" }}
                />
              )}

            {waitForUserReply && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Button Reply Options */}
                {selectedTemplate &&
                  selectedTemplate?.buttons &&
                  selectedTemplate?.buttons
                    ?.filter((button) =>
                      ["Interested", "Not Interested"].includes(button?.text)
                    )
                    ?.map((button) => (
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={2}>
                          <TextField
                            label="Button Text"
                            value={button?.text || ""}
                            disabled
                          />
                          <Autocomplete
                            sx={{ width: "100%" }}
                            options={BUTTON_REPLY_OPTIONS}
                            value={buttonReplyOptions[button?.id] || null}
                            onChange={(event, newValue) =>
                              setButtonReplyOptions((prevOptions) => ({
                                ...prevOptions,
                                [button?.id]: newValue,
                              }))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Button Action"
                                variant="outlined"
                                required
                              />
                            )}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) =>
                              option.value === value.value
                            }
                            noOptionsText="No options found"
                          />
                        </Stack>
                        {buttonReplyOptions[button?.id]?.value ===
                          "continue_next" && (
                          <Typography
                            variant="body2"
                            sx={{ fontStyle: "italic" }}
                          >
                            The next template will be sent{" "}
                            <strong>{delay} hours</strong> after the user
                            presses <strong>{button?.text}</strong>
                          </Typography>
                        )}
                        {buttonReplyOptions[button?.id]?.value === "exit" && (
                          <Typography
                            variant="body2"
                            sx={{ fontStyle: "italic" }}
                          >
                            The user will exit the campaign if they press{" "}
                            <strong>{button?.text}</strong>
                          </Typography>
                        )}
                        {buttonReplyOptions[button?.id]?.value ===
                          "live_chat_exit" && (
                          <Typography
                            variant="body2"
                            sx={{ fontStyle: "italic" }}
                          >
                            The user will exit the campaign & initiate live chat
                            if they press <strong>{button?.text}</strong>
                          </Typography>
                        )}
                      </Stack>
                    ))}
              </Box>
            )}

            <Stack spacing={1}>
              {/* No Reply Option */}
              <Autocomplete
                sx={{ width: "100%" }}
                options={NO_REPLY_OPTIONS}
                value={noReplyOption}
                onChange={(event, newValue) => setNoReplyOption(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="No Reply Action"
                    variant="outlined"
                    required
                  />
                )}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                noOptionsText="No options found"
              />
              {noReplyOption?.value === "continue_next" &&
                (waitForUserReply ? (
                  <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                    The next template will be sent after{" "}
                    <strong>{delay} hours</strong> if the user does not reply
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                    The next template will be sent after{" "}
                    <strong>{delay} hours</strong>
                  </Typography>
                ))}
              {noReplyOption?.value === "exit" &&
                (waitForUserReply ? (
                  <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                    The user will exit the campaign after{" "}
                    <strong>{delay} hours</strong> if they don't reply
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                    The user will exit the campaign after{" "}
                    <strong>{delay} hours</strong>
                  </Typography>
                ))}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !canSubmit()}
        >
          {loading ? <CircularProgress size={24} /> : "Add Step"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDripStepModal;
